/**
 * Connection overlay. SPEC.md §16.2, §16.3, §16.4.
 *
 * V1 has no offline write queue, so a dropped connection means scoring genuinely
 * stops. This says so plainly, covers the controls while it is true, and goes away
 * the instant authoritative state is known — §16.3's "restore interactive controls
 * only when authoritative state is known", made literal. §16.4
 *
 * It is not dismissable: there is nothing behind it a player could usefully do,
 * and a dismissable overlay would just hide the reason their taps do nothing.
 *
 * The panel is a separate component so its once-a-second clock exists only while
 * the overlay is actually up. §44
 */

import { useRef } from 'react';
import { formatOfflineFor, type ConnectionState } from '@card-counter/shared';
import { Spinner } from '@/components/ui/Spinner';
import { useSecondsSince } from '@/hooks/useSecondsSince';
import { useSession } from '@/hooks/useSession';
import { useConnectionState, useOfflineSince } from '@/stores/connection-store';
import { useSessionInfo } from '@/stores/session-store';

/** After this long, "it will come back" stops being credible on its own. §16.1 */
const RELOAD_HINT_AFTER_SECONDS = 15;

interface Copy {
  title: string;
  detail: string;
}

const JOINING: Copy = {
  title: 'Joining the table…',
  detail: 'Fetching the scores so far. Nothing can be entered until they arrive.',
};

const OFFLINE: Copy = {
  title: "You're offline",
  detail:
    'Scoring is paused until your connection comes back. Nothing is lost — every point lives on the server.',
};

const RESYNCING: Copy = {
  title: 'Catching up…',
  detail: 'Fetching the latest scores from the table. Scoring is paused for a moment.',
};

const RECONNECTING: Copy = {
  title: 'Reconnecting…',
  detail:
    'Scoring is paused until the table is back in sync. Nothing is lost — every point lives on the server.',
};

/**
 * The connection store boots as 'reconnecting', so "not ready yet" and "we lost it"
 * would otherwise share the same words. A first load has nothing to reconnect to.
 * §16.2, §16.3
 */
function describe(connection: ConnectionState, ready: boolean): Copy {
  if (connection === 'offline') return OFFLINE;
  if (!ready) return JOINING;
  if (connection === 'resyncing') return RESYNCING;
  if (connection === 'reconnecting') return RECONNECTING;
  // Connected and ready, but a resync is still outstanding. §15.2
  return RESYNCING;
}

interface PanelProps {
  copy: Copy;
  /** Null until a connection has actually been lost. §16.5 */
  offlineSince: number | null;
  elapsedIsMeaningful: boolean;
}

function Panel({ copy, offlineSince, elapsedIsMeaningful }: PanelProps) {
  const mountedAt = useRef(Date.now());
  const seconds = useSecondsSince(offlineSince ?? mountedAt.current);

  const showElapsed = offlineSince !== null && elapsedIsMeaningful && seconds >= 1;
  const showReload = offlineSince !== null && seconds >= RELOAD_HINT_AFTER_SECONDS;

  return (
    <div className="flex w-full max-w-xs flex-col items-center gap-3 rounded-card border border-line bg-surface px-5 py-6 text-center shadow-sheet">
      <Spinner size="lg" label={copy.title} className="text-accent" />

      <div role="status" aria-live="polite" className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-ink">{copy.title}</h2>
        <p className="text-sm text-ink-muted">{copy.detail}</p>
        {/* §16.5 — the same elapsed wording used for a disconnected player. */}
        {showElapsed ? (
          <p data-numeric className="text-sm text-ink-faint">
            {formatOfflineFor(seconds)}
          </p>
        ) : null}
      </div>

      {showReload ? (
        <button
          type="button"
          onClick={() => {
            window.location.reload();
          }}
          className="min-h-tap rounded-control border border-line bg-raised px-4 font-semibold text-ink transition-transform active:scale-[0.97]"
        >
          Reload the table
        </button>
      ) : null}
    </div>
  );
}

export function ReconnectOverlay() {
  const { ready, interactive } = useSession();
  const connection = useConnectionState();
  const offlineSince = useOfflineSince();
  const status = useSessionInfo()?.status ?? null;

  /**
   * An ended game is read-only, so there is nothing to protect once its final
   * state is in hand: an overlay over the final scores would only be in the way.
   * §32.1, §52
   */
  const visible = !ready || (!interactive && status !== 'ended');
  if (!visible) return null;

  return (
    // Above every sheet: while this is up, no control anywhere may be reached. §16.3
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-canvas/85 p-safe backdrop-blur-sm">
      <Panel
        copy={describe(connection, ready)}
        offlineSince={offlineSince}
        // "Offline 3s" is the wrong thing to say about a first load that has simply
        // not finished; it belongs to a connection that existed and went away. §16.5
        elapsedIsMeaningful={ready || connection === 'offline'}
      />
    </div>
  );
}
